// Single source of truth for the lessons promo (sale) pricing rule.
// Imported by the frontend (src/lib/lessonsPromo.ts) and by the lesson
// checkout edge functions. Keep this file free of Deno/browser APIs.

export const LESSON_REGULAR_FALLBACK = 49.9;

export interface LessonPricing {
  /** Amount that must be charged / displayed as the current price. */
  amount: number;
  /** Full (non-sale) price. */
  regularPrice: number;
  /** True when the promo is active and the sale price is lower. */
  onSale: boolean;
  /** Rounded discount percentage, e.g. 41. Zero when not on sale. */
  discountPercent: number;
}

/** Reads the raw store_settings value for `lessons_promo_until` into an ISO string or null. */
export function parsePromoUntil(raw: unknown): string | null {
  if (raw == null) return null;
  let value: unknown = raw;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "null") return null;
    // Some rows store the value JSON-encoded ("\"2026-10-01...\"")
    if (trimmed.startsWith("{") || trimmed.startsWith("\"")) {
      try {
        value = JSON.parse(trimmed);
      } catch {
        value = trimmed;
      }
    }
  }
  if (value && typeof value === "object") {
    value = (value as Record<string, unknown>).until ?? null;
  }
  if (typeof value !== "string" || !value.trim()) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

export function isPromoActive(promoUntil: string | null, now: Date = new Date()): boolean {
  if (!promoUntil) return false;
  const end = new Date(promoUntil).getTime();
  if (isNaN(end)) return false;
  return now.getTime() < end;
}

/**
 * Resolves the price to charge/display for a lesson.
 * Sale price applies ONLY while the promo deadline is in the future.
 */
export function resolveLessonPrice(
  lesson: { price?: number | null; regular_price?: number | null; available_until?: string | null },
  promoUntil: string | null,
  now: Date = new Date(),
): LessonPricing {
  const regularPrice = Number(lesson.regular_price ?? LESSON_REGULAR_FALLBACK);
  const salePrice = Number(lesson.price ?? regularPrice);

  const lessonWindowOpen =
    !lesson.available_until || new Date(lesson.available_until).getTime() > now.getTime();

  const active = isPromoActive(promoUntil, now) && lessonWindowOpen && salePrice > 0 && salePrice < regularPrice;

  if (!active) {
    return { amount: regularPrice, regularPrice, onSale: false, discountPercent: 0 };
  }

  return {
    amount: salePrice,
    regularPrice,
    onSale: true,
    discountPercent: Math.round((1 - salePrice / regularPrice) * 100),
  };
}

const LT_MONTHS = [
  "sausio", "vasario", "kovo", "balandžio", "gegužės", "birželio",
  "liepos", "rugpjūčio", "rugsėjo", "spalio", "lapkričio", "gruodžio",
];

export function formatPromoDate(promoUntil: string | null): string {
  if (!promoUntil) return "";
  const d = new Date(promoUntil);
  if (isNaN(d.getTime())) return "";
  return `${LT_MONTHS[d.getMonth()]} ${d.getDate()} d.`;
}

/** "liko 3 d. 4 val." — below 24 h: "liko 4 val. 12 min." Returns null when expired. */
export function formatPromoRemaining(promoUntil: string | null, now: Date = new Date()): string | null {
  if (!promoUntil) return null;
  const diff = new Date(promoUntil).getTime() - now.getTime();
  if (isNaN(diff) || diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  if (days > 0) return `liko ${days} d. ${hours} val.`;
  return `liko ${hours} val. ${minutes} min.`;
}

/** Full line shown under the price. Returns null when there is no active promo. */
export function formatPromoLine(promoUntil: string | null, now: Date = new Date()): string | null {
  const remaining = formatPromoRemaining(promoUntil, now);
  if (!remaining) return null;
  return `Nuolaida iki ${formatPromoDate(promoUntil)} · ${remaining}`;
}
